/**
 * A lista de organizações ligada ao servidor (#767): lê a lista com a busca e
 * o filtro, e entrega à tela.
 *
 * A tela (`TelaDeOrganizacoes`) nasceu antes do back e recebe tudo pelas
 * props; esta é quem a monta. A busca e o filtro moram na casca, e não aqui:
 * abrir uma organização desmonta a lista, e voltar dela não pode perder o que a
 * pessoa digitou.
 *
 * A busca espera a pessoa parar de digitar antes de ir ao servidor, e a lista
 * anterior fica na tela enquanto a nova não chega.
 */

import { useEffect, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { useOrganizacoesNaGestao } from '@/shared/api/consultas';
import {
  contagensDaLista,
  organizacaoNaLista,
  type SituacaoDaOrganizacao,
} from '../model/organizacao-na-lista';
import { TelaDeOrganizacoes, type TelaDeOrganizacoesProps } from './tela-de-organizacoes';

/** Quanto a busca espera a pessoa parar de digitar. */
const ESPERA_DA_BUSCA_MS = 300;

export type FiltroDasOrganizacoes = {
  readonly busca: string;
  /** Nula é "Todas". */
  readonly situacao: SituacaoDaOrganizacao | null;
};

export const FILTRO_INICIAL_DAS_ORGANIZACOES: FiltroDasOrganizacoes = { busca: '', situacao: null };

export type PaginaDeOrganizacoesProps = {
  filtro: FiltroDasOrganizacoes;
  /**
   * O `definir` da casca. Cada mudança parte do filtro mais recente, e não do
   * desta renderização: "Limpar a busca e o filtro" muda os dois de uma vez, e
   * a segunda mudança não pode desfazer a primeira.
   */
  aoFiltrar: Dispatch<SetStateAction<FiltroDasOrganizacoes>>;
  aoAbrir: TelaDeOrganizacoesProps['aoAbrir'];
  aoCriar: TelaDeOrganizacoesProps['aoCriar'];
  aoTrocarDeAba: TelaDeOrganizacoesProps['aoTrocarDeAba'];
};

export function PaginaDeOrganizacoes({
  filtro,
  aoFiltrar,
  aoAbrir,
  aoCriar,
  aoTrocarDeAba,
}: PaginaDeOrganizacoesProps): ReactNode {
  const [termo, definirTermo] = useState(filtro.busca.trim());
  useEffect(() => {
    const relogio = setTimeout(() => definirTermo(filtro.busca.trim()), ESPERA_DA_BUSCA_MS);
    return () => clearTimeout(relogio);
  }, [filtro.busca]);

  const consulta = useOrganizacoesNaGestao(termo, filtro.situacao);

  return (
    <TelaDeOrganizacoes
      organizacoes={consulta.data?.itens.map(organizacaoNaLista)}
      contagens={consulta.data ? contagensDaLista(consulta.data) : undefined}
      erro={consulta.error}
      aoRepetir={() => void consulta.refetch()}
      atualizando={consulta.isPlaceholderData || termo !== filtro.busca.trim()}
      busca={filtro.busca}
      aoBuscar={(busca) => aoFiltrar((atual) => ({ ...atual, busca }))}
      situacao={filtro.situacao}
      aoFiltrar={(situacao) => aoFiltrar((atual) => ({ ...atual, situacao }))}
      aoAbrir={aoAbrir}
      aoCriar={aoCriar}
      aoTrocarDeAba={aoTrocarDeAba}
    />
  );
}
